import { useState, useCallback, useMemo, useEffect } from "react";
import { useDigitalSignatureContext } from "./context";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import {
  getFileName,
  getVariableName,
} from "../doc-generator/serialization/deserialization";
import {
  serializeDigitalSignatureNodeSpecific,
  deserializeDigitalSignatureNodeSpecific,
  transformUserToSigner,
} from "./serialization";
import {
  signatureTypes,
  type SignatureType,
  type FileToSign,
  type Signer,
  type SignerType,
  type SignersSelectedType,
} from "./types";
import type { NodeData } from "@/workflow_api/types/workflow";
import { NodeTypes } from "@/workflow_api/types/workflow";
import { useGetUsers } from "@/queries/users/users.api";

const getNodeIcon = (
  nodeType: string,
): { type: "fas" | "far"; name: string } => {
  switch (nodeType) {
    case NodeTypes.FORM:
      return { type: "fas", name: "bars" };
    case NodeTypes.CORRECTION:
      return { type: "fas", name: "file-edit" };
    case NodeTypes.IF:
      return { type: "fas", name: "toggle-on" };
    case NodeTypes.SWITCH:
      return { type: "fas", name: "arrows-cross" };
    case NodeTypes.MULTITHREAD:
      return { type: "fas", name: "code-branch" };
    case NodeTypes.WAIT:
      return { type: "fas", name: "hourglass-half" };
    case NodeTypes.MERGE:
      return { type: "fas", name: "code-merge" };
    case NodeTypes.ASSIGN_GROUP:
      return { type: "fas", name: "users" };
    case NodeTypes.DATA_CENTER:
      return { type: "fas", name: "table" };
    case NodeTypes.EXPEDIENTE:
      return { type: "fas", name: "folder-open" };
    case NodeTypes.NOTIFICATION:
      return { type: "fas", name: "bells" };
    case NodeTypes.CALCULATED:
      return { type: "fas", name: "function" };
    case NodeTypes.PAYMENT:
      return { type: "fas", name: "dollar-sign" };
    case NodeTypes.N8N:
      return { type: "fas", name: "hexagon-nodes" };
    case NodeTypes.INTEROPERABILITY:
      return { type: "fas", name: "diagram-nested" };
    case NodeTypes.DOC_GENERATOR:
      return { type: "fas", name: "file-lines" };
    case NodeTypes.SIGN_DOCUMENT:
      return { type: "fas", name: "signature" };
    case NodeTypes.RESOLUTION:
      return { type: "fas", name: "files" };
    default:
      return { type: "fas", name: "bars" };
  }
};

export const useDigitalSignatureNode = () => {
  const context = useDigitalSignatureContext();
  const { getDocumentsAvailableForSigning, flowId, nodes } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();
  const { mutateAsync: updateNode } = useUpdateNode(flowId);

  // Estado para evitar cargar datos múltiples veces
  const [loadedNodeId, setLoadedNodeId] = useState<string | null>(null);

  // Loading states
  const [isLoadingSignatureTypes] = useState(false);
  const [isLoadingFiles] = useState(false);
  const [isLoadingSigners] = useState(false);

  // Error states
  const [signatureTypesError] = useState<string | null>(null);
  const [filesError] = useState<string | null>(null);
  const [signersError] = useState<string | null>(null);

  const { data: usersData } = useGetUsers({
    per_page: 10000, // Obtener todos los usuarios disponibles
    status: "Activo", // Solo usuarios activos
  });

  // Obtener documentos disponibles para firmar desde el workflow
  const availableDocuments = useMemo(() => {
    if (!selectedNodeId) return [];
    const documentNodes = getDocumentsAvailableForSigning(selectedNodeId);

    // Convertir los nodos de documentos a FileToSign
    return documentNodes.map((node): FileToSign => {
      const nodeData = node.data as unknown as NodeData; // Type assertion para acceder a las propiedades

      // Intentar obtener el nombre del output del documento generado
      const outputName = getVariableName(nodeData) || {
        base: getFileName(nodeData),
      };
      const displayName =
        outputName?.base || nodeData.displayName || `Documento ${node.id}`;

      return {
        id: node.id,
        name: displayName,
        source: outputName.base || nodeData.displayName || node.id,
      };
    });
  }, [selectedNodeId, getDocumentsAvailableForSigning]);

  // Preparar datos para el DropdownList agrupados por nodo
  const documentGroups = useMemo(() => {
    if (!selectedNodeId) return [];
    const documentNodes = getDocumentsAvailableForSigning(selectedNodeId);

    // Obtener el predecesor directo
    const signNode = nodes.find((n) => n.id === selectedNodeId);
    const signNodeData = signNode?.data as unknown as NodeData;
    const directPredecessorId = signNodeData?.edge?.source;
    const directPredecessorNode = directPredecessorId
      ? nodes.find((n) => n.id === directPredecessorId)
      : null;
    const directPredecessorData =
      directPredecessorNode?.data as unknown as NodeData;

    return documentNodes.map((node) => {
      const nodeData = node.data as unknown as NodeData;
      const outputName = getVariableName(nodeData) || getFileName(nodeData);
      const displayName =
        outputName.base || nodeData.displayName || `Documento ${node.id}`;

      const isSelected = context.selectedFiles.some(
        (file) => file.id === node.id,
      );

      // Si hay un nodo intermedio (por ejemplo: Merge), se va a usar el displayName de ese nodo.
      const isDirect = directPredecessorId === node.id;
      const groupLabel = isDirect
        ? nodeData.displayName || `Nodo ${node.id}`
        : directPredecessorData?.displayName ||
          nodeData.displayName ||
          `Nodo ${node.id}`;

      // Icono
      const groupNodeType = isDirect
        ? nodeData.type
        : directPredecessorData?.type || nodeData.type;
      const groupIcon = getNodeIcon(groupNodeType);

      return {
        groupId: node.id,
        groupLabels: [groupLabel],
        icon: {
          type: groupIcon.type as "fas" | "far",
          name: groupIcon.name as string,
          className: "text-action-quiet",
        },
        items: [
          {
            label: displayName,
            value: node.id,
            type: "file",
            selected: isSelected, // Add selected state
            icon: {
              type: "far" as const,
              name: "file" as const,
            },
          },
        ],
      };
    });
  }, [
    selectedNodeId,
    getDocumentsAvailableForSigning,
    context.selectedFiles,
    nodes,
  ]);

  // Cargar datos existentes del nodo cuando se selecciona
  useEffect(() => {
    if (selectedNodeId && nodes.length > 0 && selectedNodeId !== loadedNodeId) {
      const currentNode = nodes.find((node) => node.id === selectedNodeId);
      if (
        currentNode &&
        currentNode.data &&
        (currentNode.data as unknown as NodeData).inputs
      ) {
        const deserializedData = deserializeDigitalSignatureNodeSpecific(
          currentNode.data as unknown as NodeData,
        );

        if (deserializedData) {
          // Cargar los datos en el contexto
          context.setTitle(deserializedData.title);
          context.setDescription(deserializedData.description || "");

          // Buscar y setear el tipo de firma
          const signatureType = signatureTypes.find(
            (type) => type.id === deserializedData.signatureTypeId,
          );
          if (signatureType) {
            context.setSelectedSignatureType(signatureType);
          }

          // Setear archivos seleccionados
          context.setSelectedFiles(deserializedData.filesToSign);

          // Setear firmantes
          const usersById = Object.fromEntries(
            (usersData?.data || []).map((u) => [u.id, u]),
          );

          const signers = deserializedData.signers.map((s) => {
            const primaryUser = s.primary ? usersById[s.primary] : null;
            const backupUser = s.backup ? usersById[s.backup] : null;

            return {
              primary: primaryUser
                ? transformUserToSigner(primaryUser)
                : s.primary,
              backup: backupUser ? transformUserToSigner(backupUser) : s.backup,
            };
          });

          context.setSelectedSigners(signers as SignersSelectedType);

          // Marcar como cargado
          setLoadedNodeId(selectedNodeId);
        }
      } else if (selectedNodeId !== loadedNodeId) {
        // Si es un nodo nuevo sin datos, limpiar el formulario
        context.resetForm();
        setLoadedNodeId(selectedNodeId);
      }
    }
  }, [selectedNodeId, nodes, loadedNodeId, context, usersData]);

  // Save functionality
  const saveNode = useCallback(
    async (displayName: string) => {
      const formData = context.getFormData();
      if (!formData || !selectedNodeId) {
        console.error("Cannot save: form data is invalid or no node selected");
        return;
      }

      try {
        // Obtener el nodo actual para preservar sus propiedades
        const currentNode = nodes.find((node) => node.id === selectedNodeId);
        if (!currentNode) {
          console.error("Node not found");
          return;
        }

        // Serializar los datos usando el formato específico requerido
        const serializedData = serializeDigitalSignatureNodeSpecific(
          formData,
          displayName,
          flowId,
          currentNode.position?.x || 0,
          currentNode.position?.y || 0,
          (currentNode.data as unknown as NodeData)?.order || 0,
        );

        // Actualizar el nodo usando el hook de actualización
        await updateNode({
          node_id: selectedNodeId,
          node: serializedData,
        });
      } catch (error) {
        console.error("Error saving digital signature node:", error);
      }
    },
    [context, selectedNodeId, nodes, flowId, updateNode],
  );

  return {
    // Context values
    ...context,

    // Data
    signatureTypes,
    availableFiles: availableDocuments,
    documentGroups,

    // Loading states
    isLoadingSignatureTypes,
    isLoadingFiles,
    isLoadingSigners,

    // Error states
    signatureTypesError,
    filesError,
    signersError,

    // Actions
    saveNode,
  };
};

// Hook para manejar la selección de tipo de firma
export const useSignatureTypeSelector = () => {
  const { selectedSignatureType, setSelectedSignatureType } =
    useDigitalSignatureContext();

  const handleSignatureTypeChange = useCallback(
    (type: SignatureType | null) => {
      setSelectedSignatureType(type);
    },
    [setSelectedSignatureType],
  );

  return {
    selectedSignatureType,
    onSignatureTypeChange: handleSignatureTypeChange,
  };
};

// Hook para manejar la selección de archivos
export const useFileSelector = () => {
  const { selectedFiles, setSelectedFiles } = useDigitalSignatureContext();

  const handleFilesChange = useCallback(
    (files: FileToSign[]) => {
      setSelectedFiles(files);
    },
    [setSelectedFiles],
  );

  const handleFileToggle = useCallback(
    (file: FileToSign) => {
      setSelectedFiles((prev: FileToSign[]) => {
        const isSelected = prev.some((f: FileToSign) => f.id === file.id);
        if (isSelected) {
          return prev.filter((f: FileToSign) => f.id !== file.id);
        } else {
          return [...prev, file];
        }
      });
    },
    [setSelectedFiles],
  );

  return {
    selectedFiles,
    onFilesChange: handleFilesChange,
    onFileToggle: handleFileToggle,
  };
};

// Hook para manejar la selección de firmantes
export const useSignerSelector = () => {
  const { selectedSigners, addSigner, removeSigner, updateSigner } =
    useDigitalSignatureContext();

  const handleSignersChange = useCallback((signers: Signer[]) => {
    // This function can be used for bulk changes if needed
    console.log("Signers changed:", signers);
  }, []);

  const handleSignerToggle = useCallback(
    (signer: Signer, type: SignerType, index: number) => {
      const isSelected = selectedSigners.some(
        (s) => s.primary?.id === signer.id || s.backup?.id === signer.id,
      );
      if (!isSelected) {
        addSigner(signer, type, index);
      }
    },
    [selectedSigners, addSigner],
  );

  return {
    selectedSigners,
    onSignersChange: handleSignersChange,
    onSignerToggle: handleSignerToggle,
    addSigner,
    removeSigner,
    updateSigner,
  };
};
